/**
 * SIL-2062 - per-SKU unit asking prices on a lot listing, on the buyer listing
 * detail. The server only sends `listing.unitPrices` where the viewer already
 * sees the exact price (owner, or a verified buyer on a live exact-price listing
 * that is not offers-only); everyone else gets null and this card is omitted, so
 * an unverified viewer keeps the price range only.
 */
import type { LotUnitPriceRow } from '../../dto/listing.js';
import { Card } from '../Card.js';
import { formatUsd } from '../format.js';

export function LotUnitPricesCard({ unitPrices }: { unitPrices: LotUnitPriceRow[] }) {
  if (unitPrices.length === 0) return null;
  const total = unitPrices.reduce((sum, r) => sum + Math.round(r.unitPriceUsd * 100) * r.unitCount, 0) / 100;
  return (
    <Card data-testid="lot-unit-prices">
      <h2 className="text-[15px] font-semibold text-foreground">Price per unit</h2>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="py-1 pr-3 font-medium">SKU</th>
              <th className="py-1 pr-3 text-right font-medium">Qty</th>
              <th className="py-1 pr-3 text-right font-medium">Price / unit</th>
              <th className="py-1 text-right font-medium">Line total</th>
            </tr>
          </thead>
          <tbody>
            {unitPrices.map((r) => (
              <tr key={r.skuId} className="border-t border-border">
                <td className="py-1.5 pr-3">{r.skuLabel ?? 'SKU'}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{r.unitCount}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{formatUsd(r.unitPriceUsd)}</td>
                <td className="py-1.5 text-right tabular-nums">
                  {formatUsd((Math.round(r.unitPriceUsd * 100) * r.unitCount) / 100)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border font-medium">
              <td className="py-1.5 pr-3" colSpan={3}>
                Lot total
              </td>
              <td className="py-1.5 text-right tabular-nums">{formatUsd(total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}
